import { useEffect, useState } from 'react';
import { CoreMessages, IntlMessageId, PrimitiveType } from 'locale';
import { useIntl } from 'react-intl';
import { logger } from 'utils/logger';
import enMessages from 'src/lang/compiled/en.intl.output.json';

/** @deprecated
  use useIntlModule instead or vanilla useIntl https://salteese.atlassian.net/wiki/spaces/DEV/pages/2336129025/New+approach+to+translation
  use component specific translations: https://salteese.atlassian.net/wiki/spaces/DEV/pages/3068821508/Localisation#Localisation-%E2%86%92-Keep-translations-independent-for-each-component
 */
export function useStaticGlobalIntl<T extends (IntlMessageId | [IntlMessageId, Record<string, PrimitiveType>])[]>(
  ...intlDescriptors: [...T]
): MappedTuple<T, string> {
  const { locale, formatMessage } = useIntl();
  const [descriptors, setDescriptors] = useState<[...T]>(intlDescriptors);
  // we need that unnecessary useEffect just to up to date with the latest language change
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    setDescriptors(descriptors);
  }, [descriptors.length, locale]);
  return descriptors.map((descriptor) => {
    if (typeof descriptor === 'string') {
      const defaultMessage = enMessages[descriptor as keyof typeof enMessages] as string;
      if (!defaultMessage) {
        logger.warn(`Translation not found for ID: ${String(descriptor)}`);
        return descriptor;
      }
      return formatMessage({ id: descriptor, defaultMessage });
    }
    const id = (Array.isArray(descriptor) ? descriptor[0] : descriptor).toString();
    const defaultMessage = enMessages[id as keyof typeof enMessages] as string;
    if (!defaultMessage) {
      logger.warn(`Translation not found for ID: ${id.toString()}`);
      return id;
    }
    return formatMessage({ id, defaultMessage }, Array.isArray(descriptor) ? descriptor[1] : {});
  }) as MappedTuple<T, string>;
}

export type KeysWithMessage<D extends {}> = {
  [K in keyof D]-?: D[K] extends CoreMessages ? (K extends string ? K : never) : never;
}[keyof D];

export default useStaticGlobalIntl;
